import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';

const LANGUAGES = [
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'de', label: 'DE', name: 'Deutsch' },
  { code: 'fr', label: 'FR', name: 'Français' },
  { code: 'ar', label: 'AR', name: 'العربية' },
  { code: 'ru', label: 'RU', name: 'Русский' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLoginDropdown, setShowLoginDropdown] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [isWarningOpen, setIsWarningOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const langRef = useRef<HTMLDivElement>(null);
  const isDashboardPage = ['/kullanici-paneli', '/kurum-paneli', '/yonetici-paneli'].includes(location.pathname) || location.pathname.startsWith('/kurum/');
  const { user, userType, userData, signOut } = useAuth();

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowLoginDropdown(false);
    setShowUserMenu(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      setShowLoginDropdown(false);
      setShowUserMenu(false);
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setShowLangDropdown(false);
      }
    };
    if (showLoginDropdown || showUserMenu || showLangDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showLoginDropdown, showUserMenu, showLangDropdown]);

  useEffect(() => {
    const navbar = document.getElementById('site-navbar');
    if (!navbar) return;
    const updateHeight = () => {
      const height = navbar.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--navbar-height', `${height}px`);
    };
    updateHeight();
    const ro = new ResizeObserver(updateHeight);
    ro.observe(navbar);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const handleLangChange = (code: string) => {
    i18n.changeLanguage(code);
    setShowLangDropdown(false);
    localStorage.setItem('i18n_lang', code);
  };

  const getUserDisplayName = () => {
    if (userType === 'institution') return userData?.name || t('nav_institution_label');
    if (userType === 'admin') return 'Platform Admin';
    if (userType === 'user') return userData?.full_name || user?.email?.split('@')[0] || t('nav_user_label');
    return t('nav_user_label');
  };

  const getUserInitials = () => {
    if (userType === 'institution') {
      const name = userData?.name || 'K';
      return name.substring(0, 2).toUpperCase();
    }
    if (userType === 'admin') return 'AD';
    if (userType === 'user') {
      const name = userData?.full_name || user?.email || 'K';
      const parts = name.split(' ');
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return name.substring(0, 2).toUpperCase();
    }
    return 'K';
  };

  const getUserColor = () => {
    if (userType === 'institution') return 'amber';
    if (userType === 'admin') return 'teal';
    return 'rose';
  };

  const getUserIcon = () => {
    if (userType === 'institution') return 'ri-hospital-line';
    if (userType === 'admin') return 'ri-shield-user-line';
    return 'ri-user-heart-line';
  };

  const getUserDashboardPath = () => {
    if (userType === 'institution') return '/kurum-paneli';
    if (userType === 'admin') return '/yonetici-paneli';
    return '/kullanici-paneli';
  };

  const getUserPanelLabel = () => {
    if (userType === 'institution') return t('nav_institution_panel');
    if (userType === 'admin') return t('nav_admin_panel');
    return t('nav_user_panel');
  };

  const getUserTypeLabel = () => {
    if (userType === 'institution') return t('nav_institution_label');
    if (userType === 'admin') return t('nav_admin_label');
    return t('nav_user_label');
  };

  const navLinks = [
    { path: '/', label: t('nav_home') },
    { path: '/turkiye-hakkinda', label: t('nav_about_turkey') },
    { path: '/turkiye-kulturu', label: t('nav_turkey_culture') },
    { path: '/saglik-kurumlari', label: t('nav_health_institutions') },
    { path: '/araci-kurumlar', label: t('nav_agencies') },
    { path: '/iletisim', label: t('nav_contact') },
  ];

  const isActive = (path: string) => location.pathname === path;
  const color = getUserColor();

  return (
    <nav
      id="site-navbar"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{ background: '#ffffff', boxShadow: '0 2px 20px 0 rgba(0,0,0,0.06)' }}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-28">
          {/* Logo */}
          <Link to="/" className="flex items-center group flex-shrink-0">
            <div className="w-72 h-28 flex items-center justify-start flex-shrink-0">
              <img
                src="https://static.readdy.ai/image/bfc79f7fd8a7c30d01849985af6cf205/f8d8b7469b2153fc0f5999277b98f4f1.png"
                alt="Health Travel Türkiye Logo"
                className="w-72 h-28 object-contain group-hover:scale-105 transition-transform duration-300 rounded-lg"
              />
            </div>
          </Link>

          {/* Desktop navigation */}
          <div className="hidden lg:flex items-center space-x-1 ml-8 flex-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-3 py-2 text-sm font-medium transition-all rounded-lg whitespace-nowrap ${
                  isActive(link.path)
                    ? 'text-rose-500 bg-rose-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop right actions */}
          <div className="hidden lg:flex items-center space-x-2 ml-auto flex-shrink-0">

            {/* Language Selector */}
            <div className="relative" ref={langRef}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLangDropdown(!showLangDropdown);
                }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 border border-gray-200 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-global-line text-base text-gray-500"></i>
                <span>{currentLang.label}</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${showLangDropdown ? 'rotate-180' : ''}`}></i>
              </button>

              {showLangDropdown && (
                <div className="absolute right-0 top-full mt-2 w-44 bg-white rounded-xl border border-gray-100 overflow-hidden z-50"
                  style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLangChange(lang.code)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer text-left ${
                        currentLang.code === lang.code
                          ? 'bg-rose-50 text-rose-600 font-semibold'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="font-bold text-xs w-6 text-center">{lang.label}</span>
                      <span>{lang.name}</span>
                      {currentLang.code === lang.code && (
                        <i className="ri-check-line ml-auto text-rose-500 text-xs"></i>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {user ? (
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowUserMenu(!showUserMenu);
                  }}
                  className={`flex items-center space-x-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                    color === 'rose' ? 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100' :
                    color === 'amber' ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100' :
                    'border-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100'
                  }`}
                >
                  <div className="text-left">
                    <p className="font-semibold leading-tight">{getUserDisplayName()}</p>
                    <p className={`text-xs ${
                      color === 'rose' ? 'text-rose-500' :
                      color === 'amber' ? 'text-amber-500' :
                      'text-teal-500'
                    }`}>{getUserTypeLabel()}</p>
                  </div>
                  <i className={`ri-arrow-down-s-line text-base transition-transform ${showUserMenu ? 'rotate-180' : ''}`}></i>
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl border border-gray-100 overflow-hidden"
                    style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900">{getUserDisplayName()}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{user.email}</p>
                    </div>
                    <Link
                      to={getUserDashboardPath()}
                      className={`flex items-center space-x-3 px-4 py-3.5 text-sm font-semibold transition-colors ${
                        color === 'rose' ? 'bg-rose-50 text-rose-700 hover:bg-rose-100 border-l-4 border-rose-400' :
                        color === 'amber' ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border-l-4 border-amber-400' :
                        'bg-teal-50 text-teal-700 hover:bg-teal-100 border-l-4 border-teal-400'
                      }`}
                    >
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg text-white ${
                        color === 'rose' ? 'bg-gradient-to-br from-rose-400 to-rose-500' :
                        color === 'amber' ? 'bg-gradient-to-br from-amber-400 to-amber-500' :
                        'bg-gradient-to-br from-teal-400 to-teal-500'
                      }`}>
                        <i className={`${getUserIcon()} text-base`}></i>
                      </div>
                      <div>
                        <p className="font-semibold">{t('nav_my_panel')}</p>
                        <p className={`text-xs font-normal ${
                          color === 'rose' ? 'text-rose-500' :
                          color === 'amber' ? 'text-amber-500' :
                          'text-teal-500'
                        }`}>{getUserPanelLabel()}</p>
                      </div>
                      <i className={`ri-arrow-right-s-line ml-auto text-lg ${
                        color === 'rose' ? 'text-rose-400' :
                        color === 'amber' ? 'text-amber-400' :
                        'text-teal-400'
                      }`}></i>
                    </Link>
                    <div className="border-t border-gray-100"></div>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-logout-box-r-line text-lg"></i>
                      <span className="font-medium">{t('nav_logout')}</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowLoginDropdown(!showLoginDropdown);
                    }}
                    className="flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap text-gray-700 hover:bg-gray-50 border border-gray-200"
                  >
                    <i className="ri-user-line text-base"></i>
                    <span>{t('nav_login')}</span>
                    <i className={`ri-arrow-down-s-line text-base transition-transform ${showLoginDropdown ? 'rotate-180' : ''}`}></i>
                  </button>

                  {showLoginDropdown && (
                    <div className="absolute right-0 top-full mt-2 w-60 bg-white rounded-xl border border-gray-100 overflow-hidden"
                      style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
                      <Link
                        to="/kullanici-paneli"
                        className="flex items-center space-x-3 px-5 py-3.5 text-sm text-gray-700 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                      >
                        <div className="w-8 h-8 flex items-center justify-center bg-rose-100 rounded-lg">
                          <i className="ri-user-heart-line text-rose-500"></i>
                        </div>
                        <div>
                          <p className="font-medium">{t('nav_user_login')}</p>
                          <p className="text-xs text-gray-400">{t('nav_user_login_desc')}</p>
                        </div>
                      </Link>
                      <div className="border-t border-gray-100"></div>
                      <Link
                        to="/kurum-paneli"
                        className="flex items-center space-x-3 px-5 py-3.5 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors"
                      >
                        <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg">
                          <i className="ri-hospital-line text-amber-500"></i>
                        </div>
                        <div>
                          <p className="font-medium">{t('nav_institution_login')}</p>
                          <p className="text-xs text-gray-400">{t('nav_institution_login_desc')}</p>
                        </div>
                      </Link>
                      <div className="border-t border-gray-100"></div>
                      <Link
                        to="/yonetici-paneli"
                        className="flex items-center space-x-3 px-5 py-3.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-600 transition-colors"
                      >
                        <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-lg">
                          <i className="ri-shield-user-line text-teal-500"></i>
                        </div>
                        <div>
                          <p className="font-medium">{t('nav_admin_login')}</p>
                          <p className="text-xs text-gray-400">{t('nav_admin_login_desc')}</p>
                        </div>
                      </Link>
                    </div>
                  )}
                </div>

                <Link
                  to="/uye-ol"
                  className="px-5 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer bg-gradient-to-r from-rose-500 to-rose-400 text-white shadow-md shadow-rose-200/50 hover:shadow-lg hover:shadow-rose-300/50"
                >
                  {t('nav_register')}
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer text-gray-700 hover:bg-gray-100 ml-auto"
          >
            <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-xl">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  isActive(link.path)
                    ? 'bg-rose-50 text-rose-500'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Mobile Language Selector */}
            <div className="pt-2 pb-1">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-4 mb-2">Language</p>
              <div className="flex flex-wrap gap-2 px-4">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLangChange(lang.code)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                      currentLang.code === lang.code
                        ? 'bg-rose-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-3 border-t border-gray-100 space-y-2">
              {user ? (
                <>
                  <div className={`px-4 py-3 rounded-lg ${
                    color === 'rose' ? 'bg-rose-50' :
                    color === 'amber' ? 'bg-amber-50' :
                    'bg-teal-50'
                  }`}>
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 flex items-center justify-center rounded-lg text-white font-bold text-sm ${
                        color === 'rose' ? 'bg-gradient-to-br from-rose-400 to-rose-500' :
                        color === 'amber' ? 'bg-gradient-to-br from-amber-400 to-amber-500' :
                        'bg-gradient-to-br from-teal-400 to-teal-500'
                      }`}>
                        {getUserInitials()}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{getUserDisplayName()}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                    </div>
                  </div>
                  <Link
                    to={getUserDashboardPath()}
                    className={`flex items-center space-x-3 px-4 py-3.5 text-sm font-semibold rounded-xl transition-colors border-2 ${
                      color === 'rose' ? 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100' :
                      color === 'amber' ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100' :
                      'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100'
                    }`}
                  >
                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg text-white ${
                      color === 'rose' ? 'bg-gradient-to-br from-rose-400 to-rose-500' :
                      color === 'amber' ? 'bg-gradient-to-br from-amber-400 to-amber-500' :
                      'bg-gradient-to-br from-teal-400 to-teal-500'
                    }`}>
                      <i className={`${getUserIcon()} text-base`}></i>
                    </div>
                    <div>
                      <p>{t('nav_my_panel')}</p>
                      <p className={`text-xs font-normal ${
                        color === 'rose' ? 'text-rose-500' :
                        color === 'amber' ? 'text-amber-500' :
                        'text-teal-500'
                      }`}>{getUserPanelLabel()}</p>
                    </div>
                    <i className={`ri-arrow-right-s-line ml-auto text-lg ${
                      color === 'rose' ? 'text-rose-400' :
                      color === 'amber' ? 'text-amber-400' :
                      'text-teal-400'
                    }`}></i>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-2 px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                  >
                    <i className="ri-logout-box-r-line"></i>
                    <span>{t('nav_logout')}</span>
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/kullanici-paneli"
                    className="flex items-center space-x-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-rose-50 hover:text-rose-500 rounded-lg transition-colors"
                  >
                    <i className="ri-user-heart-line"></i>
                    <span>{t('nav_user_login')}</span>
                  </Link>
                  <Link
                    to="/kurum-paneli"
                    className="flex items-center space-x-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-amber-50 hover:text-amber-500 rounded-lg transition-colors"
                  >
                    <i className="ri-hospital-line"></i>
                    <span>{t('nav_institution_login')}</span>
                  </Link>
                  <Link
                    to="/yonetici-paneli"
                    className="flex items-center space-x-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-500 rounded-lg transition-colors"
                  >
                    <i className="ri-shield-user-line"></i>
                    <span>{t('nav_admin_login')}</span>
                  </Link>
                  <Link
                    to="/uye-ol"
                    className="block px-4 py-3 text-sm font-semibold bg-gradient-to-r from-rose-500 to-rose-400 text-white rounded-lg text-center whitespace-nowrap"
                  >
                    {t('nav_register')}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Warning Banner */}
      {!isDashboardPage && (
        <div style={{ background: '#f3f4f6' }} className="border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5">
            <div className="hidden lg:flex items-start gap-2">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-error-warning-line text-amber-500 text-base"></i>
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">
                <span className="font-semibold text-gray-700">{t('nav_warning_title')}:</span>{' '}
                {t('nav_warning_text')}
              </p>
            </div>

            <div className="lg:hidden">
              <button
                onClick={() => setIsWarningOpen(!isWarningOpen)}
                className="w-full flex items-center justify-between gap-2 cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-error-warning-line text-amber-500 text-base"></i>
                  </div>
                  <span className="text-sm font-semibold text-gray-700">{t('nav_warning_title')}</span>
                </div>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`${isWarningOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-gray-500 text-base transition-transform duration-200`}></i>
                </div>
              </button>
              {isWarningOpen && (
                <p className="text-xs text-gray-500 leading-relaxed mt-2 pl-7">
                  {t('nav_warning_text')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
